import React from 'react';
import { Grid, styled } from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { UseFormReturn } from 'react-hook-form';
import * as referenceSlice from '../../../data/reference.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import TextInput from '../../form/TextInput';
import SelectMenu from '../../form/SelectMenu';
import {
  ChargeType,
  ChargeTypeInstance,
} from '../../../data/models/charge-type.model';
import { PricingFunction } from '../../../data/models/pricing-function.model';
import stringUtils from '../../../services/stringUtils';
import providerUtils from '../../../services/providerUtils';

const PREFIX = 'ChargeTypeItem';

export const classes = {
  gridItem: `${PREFIX}-gridItem`,
  selectContainer: `${PREFIX}-select-container`,
};

export const StyledGrid = styled(Grid)(() => ({
  [`&.${classes.gridItem}`]: {
    alignItems: 'center',
    display: 'flex',
  },
  [`&.${classes.selectContainer} > .MuiFormControl-root`]: {
    display: 'flex',
  },
}));

export default function ChargeTypeItem({
  chargeType,
  formHook,
  idx,
}: {
  chargeType: ChargeTypeInstance;
  formHook: UseFormReturn<any, any>;
  idx: number;
}) {
  const t = useCommonTranslation();
  const requiredFieldMessage = t('common.requiredField');

  const chargeTypes = useAppSelector(referenceSlice.selectChargeTypes);
  const pricingFunctions = useAppSelector(
    accountingSlice.selectAllPricingFunctions,
  );

  const getChargeType = (chargeTypeBC: string): ChargeType | undefined => {
    return chargeTypes.find((c) => c.chargeTypeBC === chargeTypeBC);
  };

  const getPricingFunction = (
    pricingFunctionBC: string,
  ): PricingFunction | undefined => {
    return pricingFunctions.find(
      (c) => c.pricingFunctionBC === pricingFunctionBC,
    );
  };

  const ct = getChargeType(chargeType.chargeTypeBC);
  const pricingFunction = getPricingFunction(chargeType.pricingFunctionBC);
  const params =
    pricingFunction?.parameters.filter((p) => p.type === 'UserSupplied') || [];
  const isClientPassthrough =
    pricingFunction?.pricingFunctionBC === 'ClientPassthrough';
  const isProviderPassthrough =
    pricingFunction?.pricingFunctionBC === 'ProviderPassthrough';

  const roundingOptions = [
    { label: t('provider.pricingWhole'), value: 'whole' },
    { label: t('provider.pricingHalf'), value: 'half' },
    { label: t('provider.pricingTenth'), value: 'tenth' },
  ];

  const passthroughPricingFunctionName = pricingFunction
    ? providerUtils.getPricingFunctionName(
        pricingFunctions,
        pricingFunction.pricingFunctionBC,
      )
    : '';

  return (
    <React.Fragment key={`${idx}`}>
      <StyledGrid className={classes.gridItem} item xs={4}>
        {ct?.chargeTypeName || ''}
      </StyledGrid>
      {isClientPassthrough ||
        (isProviderPassthrough && (
          <StyledGrid className={classes.gridItem} item xs={8}>
            {passthroughPricingFunctionName}
          </StyledGrid>
        ))}

      <>
        {params.map((p) => {
          const isRoundTo = p.name === 'roundTo';
          return isRoundTo ? (
            <StyledGrid
              className={classes.selectContainer}
              item
              xs={4}
              key={`${idx}_${p.name}`}>
              <SelectMenu
                label={stringUtils.camelCaseToWords(p.name)}
                name={`params[${idx}].${p.name}`}
                values={roundingOptions}
                valueAttr="value"
                labelAttr="label"
                required={true}
                control={formHook.control}
                rules={{ required: requiredFieldMessage }}
              />
            </StyledGrid>
          ) : (
            <Grid item xs={4} key={`${idx}_${p.name}`}>
              <TextInput
                name={`params[${idx}].${p.name}`}
                label={stringUtils.camelCaseToWords(p.name)}
                control={formHook.control}
                required
                inputProps={{ type: 'number' }}
                rules={{ required: requiredFieldMessage }}
              />
            </Grid>
          );
        })}
        {params.length === 1 && <Grid item xs={3} />}
      </>
    </React.Fragment>
  );
}
